import React, { useState, useEffect, useCallback } from 'react';
import FileExplorer, { FileNode, SyncStatus } from '../FileExplorer';
import CodeEditor from '../CodeEditor';
import { Permissions } from '../../constants';
import { versionControlApi } from '../../services/versionControlApi';

export interface CodebasePanelProps {
  projectId: string;
  onClose: () => void;
  permissions?: string[];
  initialSelectedFile?: string | null;
  onInitialFileSelected?: () => void;
  isDevelopmentInProgress?: boolean;
}

const CodebasePanel: React.FC<CodebasePanelProps> = ({ projectId, onClose, permissions, initialSelectedFile, onInitialFileSelected, isDevelopmentInProgress = false }) => {
  const [selectedFile, setSelectedFile] = useState<FileNode | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [editorRefreshTrigger, setEditorRefreshTrigger] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ has_remote: false, ahead: 0, behind: 0, has_uncommitted_changes: false });
  const hasUpdatePermission = permissions?.includes(Permissions.UpdateProjectPermission);
  const hasPendingPull = syncStatus.has_remote && syncStatus.behind > 0;

  const fetchSyncStatus = useCallback(async () => {
    try {
      const status = await versionControlApi.gitSyncStatus(projectId);
      setSyncStatus(status);
    } catch {
      // Gitea may not be available yet
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchSyncStatus();
    }
  }, [projectId, fetchSyncStatus]);

  const handleFileSelect = (file: FileNode) => {
    setSelectedFile(file);
  };

  const handlePanelClose = () => {
    onClose();
  };

  const handleFileSave = () => {
    setRefreshTrigger(prev => !prev);
  };

  const handlePullComplete = () => {
    setEditorRefreshTrigger(prev => !prev);
    fetchSyncStatus();
  };

  return (
    <div className="flex flex-row h-full w-full overflow-hidden bg-bg-primary">
      {/* File Explorer - Left Side */}
      <FileExplorer
        projectId={projectId}
        onFileSelect={handleFileSelect}
        selectedFile={selectedFile?.path}
        refreshTrigger={refreshTrigger}
        initialSelectedFile={initialSelectedFile}
        onInitialFileSelected={onInitialFileSelected}
        isDevelopmentInProgress={isDevelopmentInProgress}
        readOnly={!hasUpdatePermission}
        permissions={permissions}
        syncStatus={syncStatus}
        onPullComplete={handlePullComplete}
        onRefreshSync={fetchSyncStatus}
      />

      {/* Code Editor - Right Side */}
      <CodeEditor
        file={selectedFile}
        projectId={projectId}
        onFileSave={handleFileSave}
        onFileClose={handlePanelClose}
        readOnly={!hasUpdatePermission || isDevelopmentInProgress}
        permissions={permissions}
        isDevelopmentInProgress={isDevelopmentInProgress}
        hasPendingPull={hasPendingPull}
        refreshTrigger={editorRefreshTrigger}
        onRefreshSync={fetchSyncStatus}
      />
    </div>
  );
};

export default CodebasePanel;

